import { useNavigate } from "react-router-dom";
import { linkKeyboardProps } from "../utils/keyboardActivation";
import { ALERT_VIDEOS } from "../utils/constants";
import Button from "./Common/Button";
import AlertIcon from "./Common/AlertIcon";
import ConnectionStatusIcon from "./Common/ConnectionStatusIcon";

const AlarmCard = ({ alarm, index, timeZone, navigationState }) => {
  const navigate = useNavigate();
  const rowId = alarm?.unit?.serialNumber ?? index;
  const getAlertVideoUrl = (alertType) => {
    const normalizedType = alertType?.toLowerCase().trim() || "";
    if (normalizedType === "air filter") {
      return ALERT_VIDEOS.AIR_FILTER_VIDEO_URL;
    }
    if (normalizedType.startsWith("condensate")) {
      return ALERT_VIDEOS.CONDENSATE_VIDEO_URL;
    }
    return null;
  };
  const alertVideoUrl = getAlertVideoUrl(alarm?.type);

  const handleOpenAlertVideo = (event) => {
    event.stopPropagation();
    if (!alertVideoUrl) return;
    window.open(alertVideoUrl, "_blank", "noopener,noreferrer");
  };

  const handleNavigateToUnit = () => {
    if (!alarm?.unit?.serialNumber) return;
    navigate(`/units/${alarm.unit.serialNumber}`, { state: navigationState });
  };

  return (
    <>
      <div
        className="alarm bg-white dark:darkThemeGradiantBg px-4 py-3 rounded-lg shadow-appShadow min-h-48 cursor-pointer"
        key={alarm?.unit?.serialNumber}
        data-testid={`alarm-card-${rowId}`}
        {...linkKeyboardProps(handleNavigateToUnit)}
      >
        <div className="flex flex-col h-full gap-3">
          <div className="alarmsBody flex grow">
            <div className="flex flex-col gap-4 w-full">
              <div className="flex justify-between gap-3">
                <div className="flex flex-col w-3/4">
                  <p className="podName text-dark-grey dark:text-white font-semibold text-base leading-5 lg:text-lg lg:leading-6 tracking-[0.27px] truncate" data-testid={`alarm-card-name-${rowId}`}>
                    {alarm?.unit?.metadata?.name}
                  </p>
                  <p className="text-dark-grey dark:text-white text-sm leading-5 font-normal tracking-[0.021px]" data-testid={`alarm-card-serial-${rowId}`}>
                    {alarm?.unit?.serialNumber}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="podName text-dark-grey dark:text-white font-semibold text-xs leading-4 lg:text-sm lg:leading-5 tracking-[0.21px] text-right" data-testid={`alarm-card-started-time-${rowId}`}>
                    {new Date(alarm?.startedAt).toLocaleTimeString("en-US", {
                      timeZone: timeZone,
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </p>
                  <p className="text-dark-grey dark:text-white text-xs leading-4 lg:text-sm lg:leading-5 font-normal tracking-[0.021px] text-right" data-testid={`alarm-card-started-date-${rowId}`}>
                    {new Date(alarm?.startedAt).toLocaleDateString("en-US", {
                      timeZone: timeZone,
                      month: "2-digit",
                      day: "2-digit",
                      year: "2-digit",
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <p className="text-dark-grey dark:text-white text-xs leading-4 lg:text-sm font-normal lg:leading-5 tracking-[0.021px] flex-1" data-testid={`alarm-card-message-${rowId}`}>
                  {alarm?.message}
                </p>
                {alertVideoUrl && (
                  <div className="flex shrink-0 pl-3">
                    <Button
                      className="inline-flex items-center justify-center text-theme hover:text-theme/80 transition-colors duration-200"
                      onClick={handleOpenAlertVideo}
                      aria-label="Watch instructional video"
                      title="Watch instructional video"
                      data-testid={`alarm-card-video-button-${rowId}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M6.5 5.75L10.25 8L6.5 10.25V5.75Z" fill="currentColor" />
                      </svg>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="alarmsFooter border-t border-grey basis-9">
            <div className="flex items-center justify-between h-full pt-3">
              <div className="flex items-center gap-6">
                <div className={`flex items-center gap-2 bg-${alarm.severity} px-2.5 py-1 rounded-[50px]`} data-testid={`alarm-card-severity-${rowId}`}>
                  <div className="iconHolder">
                    <AlertIcon severity={alarm.severity} strokeWhite />
                  </div>
                  <span className="text-white text-xs leading-5 lg:text-sm lg:leading-6 font-medium tracking-[0.021px]">
                      {alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
                  </span>
                </div>
                <ConnectionStatusIcon
                  connected={alarm?.unit?.connected === true}
                  connectionType={alarm?.unit?.connectionType}
                  hasActiveAlert={alarm?.resolvedAt == null}
                  data-testid={`connection-status-${alarm?.unit?.serialNumber || "alarm"}-${index}`}
                />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  {alarm?.resolvedAt != null && (
                    <p className="text-resolved text-xs leading-4 lg:text-sm lg:leading-5 font-normal tracking-[0.021px]" data-testid={`alarm-card-resolved-date-${rowId}`}>
                      {new Date(alarm?.resolvedAt).toLocaleDateString("en-US", {
                        timeZone: timeZone,
                        month: "2-digit",
                        day: "2-digit",
                        year: "2-digit",
                      })}
                    </p>
                  )}
                  {alarm?.resolvedAt != null && (
                    <p className="text-resolved text-xs leading-4 lg:text-sm lg:leading-5 font-semibold tracking-[0.021px]" data-testid={`alarm-card-resolved-time-${rowId}`}>
                      {new Date(alarm?.resolvedAt).toLocaleTimeString("en-US", {
                        timeZone: timeZone,
                        hour12: false,
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </p>
                  )}
                </div>

                <div
                  className={`alarmMessage font-medium text-right text-xs leading-5 lg:text-sm lg:leading-6 tracking-[0.021px] max-w-44 truncate text-${alarm.severity}`}
                  title={alarm?.type}
                  data-testid={`alarm-card-type-${rowId}`}
                >
                  {alarm?.type}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlarmCard;
